import { BehaviorParameters } from 'rangeConfig/@types';
import { CoreApi, PartKey, Product, ProductType } from '@inter-ikea-storageone/range';
import getPanelArrangement from '../widgets/panelCustomisation/utils/getPanelArrangement';
import panelLayoutConfig from '../widgets/panelCustomisation/panelLayoutConfig';
import {
  CustomSlidingDoorComponent,
  CustomizedSlidingDoorComponent
} from '@inter-ikea-storageone/range-pax';
import { Entity, PartPair, ReplaceAssemblyPartUtils } from '@inter-ikea-core/core';

/**
 * For a secified sliding door frame find the corresponding frame that fits the specified
 * sliding door
 */
const getCorrespondingFrameForSlidingDoor = (
  frame: Product,
  slidingDoor: Entity,
  allProducts: Product[],
  coreApi: CoreApi
) => {
  const frameOnSlidingDoor = coreApi.entities.getDescendantsWithProductType(
    slidingDoor,
    frame.type
  )[0];
  const frameSize = frameOnSlidingDoor && coreApi.entities.getEntityComSize(frameOnSlidingDoor);
  if (!frameSize) {
    return null;
  }
  const needsSoftClosingDevice = !!frame.meta?.needsSoftClosingDevice;
  const correspondingFrames = allProducts.filter(
    product =>
      product.type === frame.type &&
      product.design === frame.design &&
      product.communicatedDimensions.width === frameSize.width &&
      product.communicatedDimensions.height === frameSize.height
  );
  return needsSoftClosingDevice
    ? correspondingFrames.find(frame => !!frame.meta?.needsSoftClosingDevice)
    : correspondingFrames.find(frame => !frame.meta?.needsSoftClosingDevice);
};

const replaceSlidingDoorPanelFrame = ({ coreApi, props, rootState }: BehaviorParameters) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();

  if (!selectedEntity) {
    return null;
  }

  // productId is for the rails attached to the frame but also the two corresponding door frames need to be replaced
  const FRAME_RAIL_SUFFIX = 'FRM';
  const LEFT_DOOR_FRAME_SUFFIX = 'LDR';
  const RIGHT_DOOR_FRAME_SUFFIX = 'RDR';
  const panelArrangement = getPanelArrangement(selectedEntity, coreApi);
  const layout = panelLayoutConfig.find(
    panelLayout => panelLayout.arrangement === panelArrangement
  );
  const frameProduct = rootState.products.inMarket.find(
    p => p.coreProductId === props.coreProductId
  );
  if (!layout || !frameProduct) {
    return;
  }

  const needsSoftClosingDevice = !!frameProduct?.meta?.needsSoftClosingDevice;
  const softClosingDeviceProductId =
    rootState.products.inMarket.find(product =>
      [
        ProductType.PAX_SOFT_CLOSING_DEVICE_SLIDING_DOORS,
        ProductType.PAX_SOFT_OPENING_CLOSING_DEVICE_SLIDING_DOORS
      ].includes(product.type)
    )?.coreProductId || '';

  const customSlidingDoors = coreApi.entities.getByComponent(CustomSlidingDoorComponent.id);
  const slidingDoors = customSlidingDoors.length > 1 ? customSlidingDoors : [selectedEntity];
  // For custom sliding doors we change frame for all at once
  slidingDoors.forEach(door => {
    const doorFrame =
      customSlidingDoors.length > 1
        ? getCorrespondingFrameForSlidingDoor(
            frameProduct,
            door,
            rootState.products.inMarket,
            coreApi
          )
        : frameProduct;
    if (doorFrame) {
      const leftDoorId = doorFrame.coreProductId.replace(FRAME_RAIL_SUFFIX, LEFT_DOOR_FRAME_SUFFIX);
      const rightDoorId = doorFrame.coreProductId.replace(
        FRAME_RAIL_SUFFIX,
        RIGHT_DOOR_FRAME_SUFFIX
      );
      ReplaceAssemblyPartUtils.replaceProductParts(
        door,
        [
          new PartPair(PartKey.SLIDING_DOOR_PANEL_FRAME_RAIL, doorFrame.coreProductId),
          new PartPair(PartKey.SLIDING_DOOR_PANEL_FRAME_LEFT, leftDoorId),
          new PartPair(PartKey.SLIDING_DOOR_PANEL_FRAME_RIGHT, rightDoorId),
          new PartPair(
            PartKey.SLIDING_DOOR_SOFT_CLOSING_DEVICE_LEFT,
            needsSoftClosingDevice ? softClosingDeviceProductId : ''
          ),
          new PartPair(
            PartKey.SLIDING_DOOR_SOFT_CLOSING_DEVICE_RIGHT,
            needsSoftClosingDevice ? softClosingDeviceProductId : ''
          )
        ],
        coreApi.getServices()
      );
      door.addComponent(new CustomizedSlidingDoorComponent(layout.arrangement));
    }
  });
};

export default replaceSlidingDoorPanelFrame;
